import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { AppId, SharedWorkerService } from '@ecam/eventq-components';
import { AgentEventsService } from './agent-events.service';

/** Coalesces rapid Sync-with-sibling presses into one open + broadcast cycle. */
@Injectable({ providedIn: 'root' })
export class SiblingAppSyncService {
  private static readonly DEBOUNCE_MS = 600;
  private static readonly OPEN_SETTLE_MS = 150;

  private readonly agentEvents = inject(AgentEventsService);
  private readonly shared = inject(SharedWorkerService, { optional: true });
  private readonly destroyRef = inject(DestroyRef);

  private debounceHandle: ReturnType<typeof setTimeout> | null = null;
  private inFlight = false;

  readonly syncing = signal(false);

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (this.debounceHandle != null) {
        clearTimeout(this.debounceHandle);
      }
    });
  }

  scheduleSync(openSibling: () => void): void {
    if (this.debounceHandle != null) {
      clearTimeout(this.debounceHandle);
    }
    this.debounceHandle = setTimeout(() => {
      this.debounceHandle = null;
      void this.runSync(openSibling);
    }, SiblingAppSyncService.DEBOUNCE_MS);
  }

  private async runSync(openSibling: () => void): Promise<void> {
    if (this.inFlight) {
      return;
    }
    this.inFlight = true;
    this.syncing.set(true);
    try {
      const siblingOpen = (await this.shared?.isAppOpen(AppId.RESOLVE).catch(() => false)) === true;
      if (siblingOpen) {
        this.agentEvents.broadcastSiblingFocus();
      } else {
        openSibling();
      }
      await new Promise((resolve) => setTimeout(resolve, SiblingAppSyncService.OPEN_SETTLE_MS));
      await this.agentEvents.broadcastStateSync();
    } finally {
      this.inFlight = false;
      this.syncing.set(false);
    }
  }
}
